'use client';

import React, { useState } from 'react';
import { AlertTriangle, Clock, CheckCircle2, ChevronDown, ChevronUp, User } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

// Backend integration point: GET /api/ehs/corrective-actions?status=open&sort=priority
const correctiveActions = [
  {
    id: 'CA-2026-089',
    incidentRef: 'INC-2026-0412',
    title: 'Replace inadequate face shield with full-face respirator — Chemical Proc. Line 3',
    priority: 'critical' as const,
    assignee: 'Priya Nair',
    department: 'Chemical Proc.',
    dueDate: '04/28/2026',
    daysOverdue: 0,
    status: 'open' as const,
    overdue: false,
  },
  {
    id: 'CA-2026-088',
    incidentRef: 'NM-2026-0386',
    title: 'Install ignition source elimination controls in solvent storage area',
    priority: 'critical' as const,
    assignee: 'Marcus Reyes',
    department: 'Chemical Proc.',
    dueDate: '04/22/2026',
    daysOverdue: 5,
    status: 'overdue' as const,
    overdue: true,
  },
  {
    id: 'CA-2026-087',
    incidentRef: 'NM-2026-0388',
    title: 'Conduct pressure relief valve inspection and recalibration on all Unit 5 vessels',
    priority: 'high' as const,
    assignee: 'James Okafor',
    department: 'Operations',
    dueDate: '04/30/2026',
    daysOverdue: 0,
    status: 'in-progress' as const,
    overdue: false,
  },
  {
    id: 'CA-2026-086',
    incidentRef: 'INC-2026-0410',
    title: 'Update electrical safety work permit SOP and retrain electrical team',
    priority: 'high' as const,
    assignee: 'Mei-Lin Chen',
    department: 'Electrical',
    dueDate: '04/25/2026',
    daysOverdue: 2,
    status: 'overdue' as const,
    overdue: true,
  },
  {
    id: 'CA-2026-085',
    incidentRef: 'NM-2026-0384',
    title: 'Upgrade atmospheric monitoring equipment in all confined space entry points',
    priority: 'high' as const,
    assignee: 'David Okonkwo',
    department: 'Maintenance',
    dueDate: '05/05/2026',
    daysOverdue: 0,
    status: 'open' as const,
    overdue: false,
  },
  {
    id: 'CA-2026-084',
    incidentRef: 'INC-2026-0411',
    title: 'Install non-slip matting and wet floor sensor in Maintenance Bay B',
    priority: 'medium' as const,
    assignee: 'Elena Vasquez',
    department: 'Maintenance',
    dueDate: '05/02/2026',
    daysOverdue: 0,
    status: 'in-progress' as const,
    overdue: false,
  },
  {
    id: 'CA-2026-083',
    incidentRef: 'NM-2026-0389',
    title: 'Implement pedestrian separation barriers in Logistics warehouse aisles 5–9',
    priority: 'medium' as const,
    assignee: 'Darnell Washington',
    department: 'Logistics',
    dueDate: '05/10/2026',
    daysOverdue: 0,
    status: 'open' as const,
    overdue: false,
  },
];

const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };

export default function OpenCorrectiveActions() {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const sorted = [...correctiveActions].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  );

  const visible = showAll ? sorted : sorted.slice(0, 5);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const markComplete = (id: string) => {
    // TODO: Backend integration — PATCH /api/ehs/corrective-actions/:id/complete
    toast.success(`Corrective action ${id} marked complete`, {
      description: 'The linked incident record has been updated.',
    });
  };

  const overdueCount = correctiveActions.filter((c) => c.overdue).length;

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">Open Corrective Actions</h3>
          <span className="text-[11px] font-600 px-2 py-0.5 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] tabular-nums">
            {correctiveActions.length} open
          </span>
          {overdueCount > 0 && (
            <span className="text-[11px] font-600 px-2 py-0.5 rounded-md bg-red-500/10 text-red-500 tabular-nums flex items-center gap-1">
              <AlertTriangle size={10} />
              {overdueCount} overdue
            </span>
          )}
        </div>
        <button
          className="text-xs text-[hsl(var(--primary))] hover:underline"
          onClick={() => toast.info('Create corrective action form coming soon')}
        >
          + Add CA
        </button>
      </div>

      <div className="divide-y divide-[hsl(var(--border))]">
        {visible.map((ca) => {
          const isExpanded = expanded.has(ca.id);
          return (
            <div
              key={ca.id}
              className={`transition-colors duration-100 hover:bg-[hsl(var(--muted)/0.4)] ${
                ca.overdue ? 'border-l-2 border-l-red-500' : ''
              }`}
            >
              <div className="flex items-start gap-3 px-5 py-3.5">
                {/* Priority dot */}
                <div
                  className={`mt-1 shrink-0 w-2 h-2 rounded-full ${
                    ca.priority === 'critical' ?'bg-red-500'
                      : ca.priority === 'high' ?'bg-orange-500'
                      : ca.priority === 'medium' ?'bg-amber-500' :'bg-emerald-500'
                  }`}
                />

                {/* Main content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-mono text-[11px] font-500 text-[hsl(var(--muted-foreground))] tabular-nums">
                          {ca.id}
                        </span>
                        <span className="text-[hsl(var(--muted-foreground))] text-[11px]">→</span>
                        <span className="font-mono text-[11px] text-[hsl(var(--primary))] tabular-nums">
                          {ca.incidentRef}
                        </span>
                      </div>
                      <p
                        className={`text-xs font-500 text-[hsl(var(--foreground))] leading-snug ${
                          !isExpanded ? 'line-clamp-1' : ''
                        }`}
                      >
                        {ca.title}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <StatusBadge variant={ca.priority} label={ca.priority.charAt(0).toUpperCase() + ca.priority.slice(1)} />
                      <StatusBadge
                        variant={ca.status}
                        label={
                          ca.status === 'in-progress' ?'In Progress'
                            : ca.status.charAt(0).toUpperCase() + ca.status.slice(1)
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                      <User size={9} />
                      {ca.assignee}
                    </span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">·</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{ca.department}</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">·</span>
                    <span
                      className={`flex items-center gap-1 text-[10px] font-600 ${
                        ca.overdue ? 'text-red-500' : 'text-[hsl(var(--muted-foreground))]'
                      }`}
                    >
                      <Clock size={9} />
                      Due {ca.dueDate}
                      {ca.overdue && ` · ${ca.daysOverdue}d overdue`}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    title="Mark corrective action as complete"
                    onClick={() => markComplete(ca.id)}
                    className="p-1.5 rounded-md hover:bg-emerald-500/10 text-[hsl(var(--muted-foreground))] hover:text-emerald-500 transition-all duration-150"
                  >
                    <CheckCircle2 size={13} />
                  </button>
                  <button
                    onClick={() => toggleExpand(ca.id)}
                    className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all duration-150"
                    aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                  >
                    {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {correctiveActions.length > 5 && (
        <div className="px-5 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-1"
          >
            {showAll
              ? 'Show fewer'
              : `Show all ${correctiveActions.length} corrective actions`}
            {showAll ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      )}
    </div>
  );
}